import {Avatar, Button, createStyles, DialogProps, Divider, makeStyles, Modal} from "@material-ui/core";
import React, {useState} from "react";
import CommentCountIcon from "../../common/components/images/CommentCountIcon";
import ContentsCountIcon from "../../common/components/images/ContentsCountIcon";
import PlayCountIcon from "../../common/components/images/PlayCountIcon";
import ReactionCountIcon from "../../common/components/images/ReactionCountIcon";
import ShareButton from "../../common/components/images/ShareButton";
import {SpacedList, SpacedListItem} from "../../common/components/spacedList/SpacedList";
import PuiTooltip from "../../components/elements/PuiTooltip ";
import PuiTypography from "../../components/elements/PuiTypography";
import {ProgramDetail, Video} from "../../stores/program/model/Program";
import styles from "./ProgramOverview.module.css";

export interface ProgramOverviewProps {
  // チャンネル
  channel: ProgramDetail;
}

interface PresenterInfo {
  name: string;
  presenterImageUrl: string;
}

const useStyles = makeStyles(() =>
    createStyles({
        presenterAvatar: {
            width: "50px",
            height: "50px",
            margin: "0 auto 5px auto"
        },
        copyButton: {
            height: "25px",
            border: "1px solid var(--gray-85)",
            backgroundColor: "rgb(237, 237, 237)",
            borderRadius: "0px"
        }
    })
);

function ProgramOverview({channel}: ProgramOverviewProps) {
    const classes = useStyles();

    const [showUrlModal, setShowUrlModal] = useState<boolean>(false);

    // プレゼンターリスト表示
    const formatStr = (episodeList: Video[]) => {
        const presenterList: PresenterInfo[] = [];
        episodeList.map((item) => {
            if (item.presenter !== "UNKNOWN") {
                presenterList.push({
                    name: item.presenter,
                    presenterImageUrl: item.presenterImageUrl
                });
            }
            return null;
        });

        const returnList = presenterList.filter((item, index, array) => {
            return array.findIndex((item2) => item.name === item2.name) === index;
        });

        return returnList;
    };

    // タグリスト表示
    const makeTags = (episodeList: Video[]) => {
        let tags: string[] = [];
        for (let index = 0; index < episodeList.length; index++) {
            tags = tags.concat(episodeList[index].tags);
        }
        tags = tags.filter((value, index) => tags.indexOf(value) === index);
        return tags;
    };

    // 再生数計算
    const calcPlayCount = (episodeList: Video[]) => {
        let cnt = 0;
        for (let index = 0; index < episodeList.length; index++) {
            cnt = cnt + episodeList[index].viewCount;
        }
        return cnt.toLocaleString();
    };
    // コメント数計算
    const calcCommentCount = (episodeList: Video[]) => {
        let cnt = 0;
        for (let index = 0; index < episodeList.length; index++) {
            cnt = cnt + episodeList[index].commentsCount;
        }
        return cnt.toLocaleString();
    };

    // リアクション数計算
    const calcReactionCount = (episodeList: Video[]) => {
        let cnt = 0;
        for (let index = 0; index < episodeList.length; index++) {
            cnt = cnt + episodeList[index].reactionsCount;
        }
        return cnt.toLocaleString();
    };

    const handleCopy = () => {
        const urlInput = document.getElementById("urlInput") as HTMLInputElement;
        urlInput.select();
        document.execCommand("copy");
    };

    const handleClickShare = () => {
        setShowUrlModal(true);
    };


    const handleCloseUrlModal:DialogProps["onClose"] = () => {

        setShowUrlModal(false);
    };

    return (
        <div className={styles.container}>
            <div className={styles.presenterTitleRow}>
                <PuiTypography size={12} bold={true}>
          プレゼンター
                </PuiTypography>
                <div className={styles.iconModalContainer} onClick={handleClickShare}>
                    <div className={styles.shareIcon}>
                        <ShareButton/>
                    </div>
                </div>
            </div>
            <div className={styles.presenterDetailRow}>
                {channel.videos.length > 0 &&
          formatStr(channel.videos)
              .slice(0, 3)
              .map((presenter) => (
                  <div
                      className={styles.presenterProfile}
                      key={presenter.name}
                      data-tip={presenter.name}
                      data-for="presenterName"
                  >
                      <Avatar src={presenter.presenterImageUrl} className={classes.presenterAvatar}/>
                      <div className={styles.presenterName}>{presenter.name}</div>
                      <PuiTooltip id={"presenterName"}/>
                  </div>
              ))}
            </div>
            <div className={styles.presenterOtherRow}>
                {formatStr(channel.videos).length > 3 && (
                    <PuiTypography size={12}>{`他${formatStr(channel.videos).length - 3}名`}</PuiTypography>
                )}
            </div>
            <Divider/>
            <div className={styles.description}>
                <PuiTypography size={12} bold={true}>
                    {channel.description}
                </PuiTypography>
            </div>
            <Divider/>
            <div className={styles.tagRow}>
                {channel.videos.length > 0 && (
                    <SpacedList>
                        {makeTags(channel.videos).map((tag) => (
                            <SpacedListItem key={tag}>
                                <div className={styles.tag}>{tag}</div>
                            </SpacedListItem>
                        ))}
                    </SpacedList>
                )}
            </div>
            <Divider/>
            <div className={styles.infoRow}>
                <div className={styles.contentsInfo}>
                    <PuiTypography size={12} bold={true}>
            コンテンツ数
                    </PuiTypography>
                    <div className={styles.contentsCountIcon}>
                        <ContentsCountIcon/>
                    </div>
                    <PuiTypography size={12} bold={true} color={"gray-61"}>
                        {channel.videos.length}
                    </PuiTypography>
                </div>
                <div className={styles.playInfo}>
                    <PuiTypography size={12} bold={true}>
            再生数
                    </PuiTypography>
                    <div className={styles.playCountIcon}>
                        <PlayCountIcon/>
                    </div>
                    <PuiTypography size={12} bold={true} color={"gray-61"}>
                        {calcPlayCount(channel.videos)}
                    </PuiTypography>
                </div>
                <div className={styles.commentInfo}>
                    <PuiTypography size={12} bold={true}>
            コメント数
                    </PuiTypography>
                    <div className={styles.commentCountIcon}>
                        <CommentCountIcon/>
                    </div>
                    <PuiTypography size={12} bold={true} color={"gray-61"}>
                        {calcCommentCount(channel.videos)}
                    </PuiTypography>
                </div>
                <div className={styles.reactionInfo}>
                    <PuiTypography size={12} bold={true}>
            リアクション数
                    </PuiTypography>
                    <div className={styles.reactionCountIcon}>
                        <ReactionCountIcon/>
                    </div>
                    <PuiTypography size={12} bold={true} color={"gray-61"}>
                        {calcReactionCount(channel.videos)}
                    </PuiTypography>
                </div>
            </div>

            <Modal open={showUrlModal} onClose={handleCloseUrlModal}>
                <div className={styles.urlModalContainer}>
                    <PuiTypography size={12} bold={true}>
            URL
                    </PuiTypography>
                    <div className={styles.urlRow}>
                        <input
                            id="urlInput"
                            type="text"
                            value={window.location.href}
                            readOnly={true}
                            className={styles.urlInput}
                        />
                        <Button onClick={handleCopy} className={classes.copyButton}>
              コピー
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default ProgramOverview;
